import React, { useState, useMemo, type ReactNode } from 'react'

import { LinkOutlined, QuestionCircleOutlined } from '@ant-design/icons'
import { useOperatorData } from '@contexts/operator/OperatorDataContext'
import useBatchOperation from '@hooks/shared/useBatchOperation'
import { useImport } from '@hooks/shared/useImport'
import ImportModal from '@shared/ImportModal'
import { MultipleValue } from '@shared/table/Rules/component'
import FullDayRangePicker from '@components/shared/FullDayRangePicker'

import TableIndex from '@shared/table/TableIndex'
import type { TableService, SupportRule, TableAction } from '@shared/table/TableIndex'
import { Tag, Tooltip, Space } from 'antd'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { downloadImportTemplate } from '@utils/downloadTemplate'

interface ReturnOrderService extends TableService {
  import: (formData: FormData) => Promise<unknown>;
  export: (ids: unknown[], language: string) => Promise<unknown>;
}

interface MerchantOption {
  id: string;
  name: string;
  [key: string]: unknown;
}

interface ReturnOrderRow {
  id: string;
  name?: string;
  merchant_id?: string;
  merchant_name?: string;
  order_id?: string;
  order_name?: string;
  status?: string;
  note?: string;
  created_at?: string;
  updated_at?: string;
  [key: string]: unknown;
}

interface ReturnOrdersListProps {
  service: ReturnOrderService; // New prop
  role?: 'admin' | 'operator';
  showMerchantColumn?: boolean;
  merchants?: MerchantOption[];
  addJob?: (job: unknown) => void;
  importModalVisible?: boolean;
  setImportModalVisible?: (visible: boolean) => void;
}

interface StatusTagProps {
  status?: string;
  t: (key: string) => string;
}

const StatusTag: React.FC<StatusTagProps> = ({ status, t }) => {
  const statusColors: Record<string, string> = {
    pending: 'orange',
    verified: 'blue',
    stored: 'green',
    canceled: 'red'
  }

  return (
    <Tag color={(status && statusColors[status]) || 'default'}>
      {t(`enums.return_order.status.${status}`)}
    </Tag>
  )
}

const ReturnOrdersList: React.FC<ReturnOrdersListProps> = ({
  service, // New prop
  role = 'admin',
  showMerchantColumn = false,
  merchants = [],
  addJob,
  importModalVisible: externalImportModalVisible,
  setImportModalVisible: externalSetImportModalVisible
}) => {
  const { t, i18n } = useTranslation()
  const [lastModified, setLastModified] = useState(0)
  const [internalImportModalVisible, setInternalImportModalVisible] = useState(false)

  // Support external or internal import modal control
  const importModalVisible = externalImportModalVisible !== undefined ? externalImportModalVisible : internalImportModalVisible
  const setImportModalVisible = externalSetImportModalVisible || setInternalImportModalVisible

  // 使用共用 batch operation hook
  const executeBatchOperation = useBatchOperation(service, addJob) as (opts: { task: () => Promise<unknown>; jobName: string }) => void
  // Get merchants from OperatorDataContext (only for operator role)
  // Always call hooks unconditionally
  const operatorData = useOperatorData() as { merchants?: MerchantOption[] } | null
  const operatorMerchants = role === 'operator' ? (operatorData?.merchants || []) : []
  const finalMerchants = role === 'operator' && operatorMerchants.length > 0 ? operatorMerchants : merchants

  // 匯入功能（使用 Context 共享背景任務管理器）
  // useImport 仍是 .js，TS 從 default arg 推出的 signature 跟實際差距大；用 unknown→具體型別繞過。
  const handleImport = (useImport as unknown as (cfg: {
    action: (formData: FormData) => Promise<unknown>;
    addJob?: (job: unknown) => void;
    jobName?: string;
  }) => (file: File, merchantId: string, overrideExisting: boolean) => Promise<unknown>)({
    action: (formData: FormData) => service.import(formData),
    addJob,
    jobName: 'shared.return_orders.list.import.job_name',
    // 注意：不在 onSuccess 重新載入表格，因為 Background Job 還在執行中
    // 使用者需要在 Background Job 完成後手動重新整理，或等待自動通知
  })

  // 處理 Modal 匯入
  const handleModalImport = async (file: File, merchantId: string, overrideExisting: boolean) => {
    await handleImport(file, merchantId, overrideExisting)
  }

  // 處理下載範例
  const handleDownloadTemplate = async (merchantId?: string) => {
    await downloadImportTemplate({
      service,
      language: i18n.language,
      merchantId,
      t,
      successKey: 'shared.return_orders.list.template.success',
      errorKey: 'shared.return_orders.list.template.error'
    })
  }

  const columns = useMemo<unknown[]>(() => {
    const baseColumns: Record<string, unknown>[] = [
      {
        nameKey: 'models.return_order.name',
        persist: true,
        sortable: true,
        sortFieldName: 'return_orders.name',
        width: '200px',
        wrap: true,
        cell: ({ id, name }: ReturnOrderRow) => (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <Link to={`/return_orders/${id}`} style={{ wordBreak: 'break-word' }}>
              {name}
            </Link>
            <a
              href={`/${role}/return_orders/${id}`}
              target="_blank"
              onClick={(e) => e.stopPropagation()}
              style={{ flexShrink: 0 }} rel="noreferrer"
            >
              <LinkOutlined style={{ fontSize: '12px', color: '#999' }} />
            </a>
          </div>
        ),
      },
    ]

    if (showMerchantColumn) {
      baseColumns.push({
        nameKey: 'models.merchant.name',
        wrap: true,
        selector: ({ merchant_id, merchant_name }: ReturnOrderRow) => merchant_name || finalMerchants.find((s) => s.id === merchant_id)?.name || '-',
      })
    }

    return [
      ...baseColumns,
      {
        nameKey: 'models.order.order_number',
        wrap: true,
        cell: ({ order_id, order_name }: ReturnOrderRow) => {
          if (!order_id || !order_name) return '-'
          return (
            <Link to={`/orders/${order_id}`}>
              {order_name}
            </Link>
          )
        },
      },
      {
        nameKey: 'models.return_order.status',
        name: (
          <Space size={4}>
            {t('models.return_order.status')}
            <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{t('shared.return_orders.list.column_tooltips.status')}</span>}>
              <QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} />
            </Tooltip>
          </Space>
        ),
        width: '120px',
        cell: ({ status }: ReturnOrderRow) => <StatusTag status={status} t={t} />,
      },
      {
        nameKey: 'models.return_order.note',
        wrap: true,
        width: '200px',
        selector: ({ note }: ReturnOrderRow) => note || '-',
      },
      {
        nameKey: 'common.created_at',
        sortable: true,
        sortFieldName: 'return_orders.created_at',
        wrap: true,
        selector: ({ created_at }: ReturnOrderRow) => created_at,
      },
      {
        nameKey: 'common.updated_at',
        sortable: true,
        sortFieldName: 'return_orders.updated_at',
        wrap: true,
        selector: ({ updated_at }: ReturnOrderRow) => updated_at,
      }
    ]
  }, [role, showMerchantColumn, finalMerchants, t])

  const supportRules = useMemo<SupportRule[]>(() => {
    const baseRules: SupportRule[] = [
      {
        label: t('models.return_order.name'),
        component: MultipleValue,
        inputName: 'return_orders.name',
        defaultPinned: true,
        createable: true,
        tooltip: {
          title: t('shared.return_orders.list.filter_tooltips.name.title'),
          content: t('shared.return_orders.list.filter_tooltips.name.content'),
        },
      },
      {
        label: t('models.order.order_number'),
        component: MultipleValue,
        inputName: 'orders.name',
        createable: true,
        tooltip: {
          title: t('shared.return_orders.list.filter_tooltips.order_number.title'),
          content: t('shared.return_orders.list.filter_tooltips.order_number.content'),
        },
      },
      {
        label: t('models.return_order.status'),
        component: MultipleValue,
        inputName: 'return_orders.status',
        createable: false,
        defaultOptions: [
          { label: t('enums.return_order.status.pending'), value: 'pending' },
          { label: t('enums.return_order.status.verified'), value: 'verified' },
          { label: t('enums.return_order.status.stored'), value: 'stored' },
          { label: t('enums.return_order.status.canceled'), value: 'canceled' },
        ],
        tooltip: {
          title: t('shared.return_orders.list.filter_tooltips.status.title'),
          content: t('shared.return_orders.list.filter_tooltips.status.content'),
        },
      },
      {
        label: t('common.created_at'),
        component: FullDayRangePicker,

        defaultPinned: true,
        inputName: 'return_orders.created_at',
        tooltip: {
          title: t('shared.return_orders.list.filter_tooltips.created_at.title'),
          content: t('shared.return_orders.list.filter_tooltips.created_at.content'),
        },
      },
    ]

    if (showMerchantColumn) {
      baseRules.splice(1, 0, {
        label: t('models.merchant.name'),
        component: MultipleValue,
        inputName: 'return_orders.merchant_id',
        defaultOptions: finalMerchants.map((merchant) => ({
          label: merchant.name,
          value: merchant.id,
        })),
        tooltip: {
          title: t('shared.return_orders.list.filter_tooltips.merchant.title'),
          content: t('shared.return_orders.list.filter_tooltips.merchant.content'),
        },
      })
    }

    return baseRules
  }, [t, showMerchantColumn, finalMerchants])

  const actions = useMemo<TableAction[]>(() => [
    {
      name: 'common.button.export',
      // className 為原本就傳遞的 prop，TableAction interface 不在意多餘欄位
      do: (selected: unknown) => executeBatchOperation({
        task: () => service.export(Array.from(selected as Iterable<unknown>), i18n.language),
        jobName: 'common.button.export'
      })
    } as unknown as TableAction,
  ], [executeBatchOperation, service, i18n])

  return (
    <>
      <TableIndex
        columns={columns}
        searchable={{ match: 'prefix', searchFields: [t('models.return_order.name'), t('models.order.order_number'), t('models.merchant.name')] }}
        columnsEditable={true}
        tableId={`${role.charAt(0).toUpperCase() + role.slice(1)}ReturnOrdersTable`}
        defaultQuery={{
          sorting_field: 'return_orders.created_at',
          sorting_direction: 'desc',
        }}
        supportRules={supportRules}
        lastModified={lastModified}
        setLastModified={setLastModified}
        actions={actions}
        service={service}
        filterHelpExample={t('shared.return_orders.list.filter_help_example') as string}
        filterDocsUrl={`/docs/${role}/return_orders/list#features`}
      />
      <ImportModal
        open={importModalVisible}
        onClose={() => setImportModalVisible(false)}
        onImport={handleModalImport}
        onDownloadTemplate={handleDownloadTemplate}
        role={role}
        merchants={finalMerchants}
        title={t('shared.return_orders.list.import.modal_title')}
      />
    </>
  )
}

export default ReturnOrdersList
